function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { ApolloLink, HttpLink, Observable } from '@apollo/client';
import { createOperation } from '@apollo/client/link/utils';
import { GraphQlError } from '@sail/data';
import wrapFetchWithNormalizeHeaders from '../wrapFetchWithNormalizeHeaders';
import wrapFetchWithErrors from '../errors/wrapFetchWithErrors';
import routeTo from '../../../schema/routeTo.json';
const defaultSubscriptionError = new GraphQlError('Subscription completed with no value');
let isDev = false;
let splitByRoute = false;
let fetchFromContext = false;
try {
    // eslint-disable-next-line no-restricted-globals
    isDev = process.env.NODE_ENV !== 'production';
} catch (e) {
// Do nothing.
}
try {
    var _window_PRELOADED_flags, _window_PRELOADED, // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _window;
    splitByRoute = isDev || typeof window !== 'undefined' && ((_window = window) === null || _window === void 0 ? void 0 : (_window_PRELOADED = _window.PRELOADED) === null || _window_PRELOADED === void 0 ? void 0 : (_window_PRELOADED_flags = _window_PRELOADED.flags) === null || _window_PRELOADED_flags === void 0 ? void 0 : _window_PRELOADED_flags.graphql_v2_multiregion);
} catch (e) {
// Do nothing.
}
try {
    var _window_PRELOADED_flags1, _window_PRELOADED1, // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _window1;
    fetchFromContext = typeof window !== 'undefined' && ((_window1 = window) === null || _window1 === void 0 ? void 0 : (_window_PRELOADED1 = _window1.PRELOADED) === null || _window_PRELOADED1 === void 0 ? void 0 : (_window_PRELOADED_flags1 = _window_PRELOADED1.flags) === null || _window_PRELOADED_flags1 === void 0 ? void 0 : _window_PRELOADED_flags1.sail_data_fetch_from_config);
} catch (e) {
// Do nothing.
}
const allowedTopLevelFields = new Set([
    'v2TestTopLevelField',
    'v2RetrieveWorkspace',
    'v2CreateSandbox',
    'v2CreateSandboxOrganization',
    'v2CreateSandboxUnderSandboxOrganization',
    'v2RetrieveSandboxOrganizationCreation',
    'inlineRetrieveUserPermissions',
    'v2SearchList',
    // Adding public preview Accounts v2 fields which are very sensitive to
    // cell-ignorant round trips.
    'v2SecondaryRoutingOfCreateAccount',
    'v2SecondaryRoutingOfRetrieveAccount',
    'v2SecondaryRoutingOfCreatePerson',
    'v2SecondaryRoutingOfUpdateAccount',
    'v2SecondaryRoutingOfUpdatePerson'
]);
function createHttpLink(routeThroughEdge, fetch, url) {
    return new HttpLink({
        fetch,
        uri: (operation)=>{
            const params = `name=${operation.operationName}`;
            return routeThroughEdge ? `/graphql${url}?${params}` : `/ajax/graphql${url}?${params}`;
        },
        includeUnusedVariables: splitByRoute
    });
}
function createErrorDecoratorLink() {
    return new ApolloLink((operation, forward)=>{
        var _forward;
        return (_forward = forward(operation)) === null || _forward === void 0 ? void 0 : _forward.map(function errorDecoratorLinkResponse(res) {
            var _res_errors;
            const { response } = operation.getContext();
            (_res_errors = res.errors) === null || _res_errors === void 0 ? void 0 : _res_errors.forEach((error)=>{
                // @ts-expect-error Allow re-defining extensions in case it doesn't exist.
                error.extensions = error.extensions || {};
                error.extensions.error = error.extensions.error || {};
                error.extensions.error.source = '<unknown>';
                error.extensions.error.status = response.status;
                Object.defineProperty(error.extensions, '__DO_NOT_USE_requestId', {
                    value: response.headers.get('Request-Id'),
                    writable: true,
                    configurable: true
                });
            });
            return res;
        });
    });
}
export default function createTerminatingLink(routeThroughEdge, fetch) {
    if (!splitByRoute) {
        return new ApolloLink(function terminatingUnflaggedLink(operation) {
            return new Observable((observer)=>{
                const context = operation.getContext();
                const fetchToUse = fetchFromContext ? wrapFetchWithNormalizeHeaders(wrapFetchWithErrors(context.fetch || fetch)) : fetch;
                const defaultRouter = createErrorDecoratorLink().concat(createHttpLink(routeThroughEdge, fetchToUse, ''));
                const observable = defaultRouter.request(operation);
                if (!observable) {
                    throw new Error('HttpLink did not return an observable');
                }
                const subscription = observable.subscribe({
                    next (value) {
                        observer.next(value);
                    },
                    error (error) {
                        observer.error(error);
                    },
                    complete () {
                        observer.complete();
                    }
                });
                return ()=>{
                    subscription.unsubscribe();
                };
            });
        });
    }
    const httpLinks = {};
    return new ApolloLink(function terminatingLink(operation) {
        return new Observable((observer)=>{
            const perRoute = {};
            const query = operation.query;
            const defs = query.definitions;
            const operationDefinition = defs.find((d)=>{
                return d.kind === 'OperationDefinition';
            });
            const nonOperationDefinitions = defs.filter((d)=>{
                return d.kind !== 'OperationDefinition';
            });
            if (!operationDefinition) {
                throw new Error('Could not detect any GraphQL operation (query or mutation)');
            }
            const subscriptions = [];
            operationDefinition.selectionSet.selections.forEach((s)=>{
                const fields = routeTo[operationDefinition.operation];
                const name = s.kind === 'Field' ? s.name.value : '';
                // If we have something that is not a field in the root selection set
                // (e.g. a fragment), set it to be always added to the root query. This
                // is sub-optimal, but guaranteed to work.
                const route = allowedTopLevelFields.has(name) && fields.hasOwnProperty(name) ? fields[name] : '';
                if (perRoute.hasOwnProperty(route)) {
                    perRoute[route].push(s);
                } else {
                    perRoute[route] = [
                        s
                    ];
                }
            });
            const promises = Object.keys(perRoute).map((route)=>{
                const newOperation = createOperation(operation.getContext(), _object_spread_props(_object_spread({}, operation), {
                    query: _object_spread_props(_object_spread({}, query), {
                        definitions: nonOperationDefinitions.concat(_object_spread_props(_object_spread({}, operationDefinition), {
                            selectionSet: _object_spread_props(_object_spread({}, operationDefinition.selectionSet), {
                                selections: perRoute[route]
                            })
                        }))
                    })
                }));
                return new Promise((resolve, reject)=>{
                    let partialResult = {
                        data: null,
                        errors: [
                            defaultSubscriptionError
                        ]
                    };
                    if (!httpLinks.hasOwnProperty(route)) {
                        const context = operation.getContext();
                        const fetchToUse = fetchFromContext ? wrapFetchWithNormalizeHeaders(wrapFetchWithErrors(context.fetch || fetch)) : fetch;
                        httpLinks[route] = createErrorDecoratorLink().concat(createHttpLink(routeThroughEdge, fetchToUse, route));
                    }
                    const observable = httpLinks[route].request(newOperation);
                    if (!observable) {
                        throw new Error('HttpLink did not return an observable');
                    }
                    subscriptions.push(observable.subscribe({
                        next (value) {
                            partialResult = value;
                        },
                        error (error) {
                            reject(error);
                        },
                        complete () {
                            resolve(partialResult);
                        }
                    }));
                });
            });
            const promise = Promise.all(promises).then((values)=>{
                let resultData = {};
                let resultErrors = [];
                values.forEach(({ data, errors })=>{
                    if (data) {
                        resultData = _object_spread({}, resultData, data);
                    }
                    if (errors) {
                        resultErrors = [
                            ...resultErrors,
                            ...errors
                        ];
                    }
                });
                const result = resultErrors.length ? {
                    data: resultData,
                    errors: resultErrors
                } : {
                    data: resultData
                };
                observer.next(result);
                observer.complete();
            });
            promise.catch((error)=>{
                observer.error(error);
            });
            return ()=>{
                subscriptions.forEach((subscription)=>{
                    subscription.unsubscribe();
                });
            };
        });
    });
}

//# sourceMappingURL=createTerminatingLink.js.map